import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';

import type { ServicoTipoResponse, SetorServico } from './servico-tipo.model';

@Injectable({ providedIn: 'root' })
export class ServicoTipoService extends BaseService {
    readonly PATH = SICOM_PATHS.servicos.tipos;

    /** Um cache por setor — dois no máximo, e o catálogo de um não invalida o do outro. */
    private readonly catalogoPorSetor = new Map<SetorServico, Observable<ServicoTipoResponse[]>>();

    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    /**
     * O catálogo de serviços do setor, cacheado e compartilhado entre os consumidores (uma requisição
     * por sessão, como o `ComissaoAssuntoService`). Em caso de erro o cache é descartado, para a
     * próxima entrada no módulo tentar de novo em vez de servir a falha para sempre.
     *
     * Quem garante o retry após erro é o próprio `shareReplay` do RxJS (reseta seu conector interno
     * ao ver um erro, então a subscrição seguinte já refaz a chamada por conta própria). O
     * `catalogoPorSetor.delete` abaixo não é o que produz esse retry — ele mantém explícita a
     * invariante do `Map`: só guarda um catálogo em voo ou bem-sucedido, nunca um que já falhou,
     * independente desse detalhe de implementação do RxJS (que já mudou entre versões majors).
     */
    listarPorSetor(setor: SetorServico): Observable<ServicoTipoResponse[]> {
        const emCache = this.catalogoPorSetor.get(setor);
        if (emCache) {
            return emCache;
        }

        const params = new HttpParams().set('tipoPessoa', setor);
        const catalogo$ = this.http.get<ServicoTipoResponse[]>(this.PATH, { params }).pipe(
            catchError((err) => {
                this.catalogoPorSetor.delete(setor);
                return throwError(() => err);
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        this.catalogoPorSetor.set(setor, catalogo$);
        return catalogo$;
    }
}
